// The case inbox: what over a search needs a person, as cases that can span
// sessions. A case starts from findings that need someone (seeds): those a
// standard met, those an audit's dig said a person should see, rare acts (it
// happened, no standard caught it, it is high or worse, and no other session
// in reach did that kind of act), and those in a case the reader kept (opened,
// dug or decided). It grows along links that can be explained, strongest
// first: what the reader put together (a kept case's sessions, and the
// sessions laid on its board); sessions where the same kind of act was
// found, when only a few sessions in reach did it; and sessions on the same
// machine within two hours of each other. A case holds at most
// CASE_MAX_SESSIONS sessions: a link that would take it past that leaves the
// other session nearby rather than in it, as does a link to a session with
// nothing that needs someone. Nothing here calls a model or the network: the
// same findings, verdicts and kept cases always make the same cases.
//
// What Investigate and the reader did with a case (its dig and notes, the
// reader's decision, an audit) is kept by case in the investigation store
// under INBOX_SCOPE, so the welcome page and every workspace read the same
// case. A formed case takes the id of the kept case holding most of its
// findings, so what was kept stays with it as the search changes.
//
// Formed in the browser over what one search loaded (at most the pool's
// ceiling of findings), so it costs a sort and a pass over that pool; a
// fleet-wide inbox would form cases on the server.
import { caseView } from "./investigateCases.js";
import { comparePlaced } from "./investigateFacts.js";
import { severityRank } from "./triageLite.js";
import { parseServerTimestamp } from "./traceUtils.js";

export const INBOX_SCOPE = "inbox";
// Sessions one case holds at most.
export const CASE_MAX_SESSIONS = 8;
// A kind of act links sessions only while it is rare in reach: one done in
// more sessions than this is a habit, not one story.
export const FAMILY_MAX_SESSIONS = 6;
// Sessions on one machine this close are one sitting.
export const MACHINE_WINDOW_MS = 2 * 3_600_000;
// Sessions a case names as nearby, the closest first.
export const NEARBY_MAX = 6;
// Rare acts the inbox raises at most, the loudest first, and only where at
// least RARE_MIN_SESSIONS sessions with findings are in reach: in a narrow
// search, "the only session that did this" says nothing.
export const RARE_MAX = 6;
export const RARE_MIN_SESSIONS = 40;
// Seeds formed at once, the most pressing first.
export const SEEDS_MAX = 800;
const RARE_SEVERITY = 3; // high and above
export const ORIGINS = ["standard", "audit", "rare", "kept"];
const ORIGIN_RANK = { standard: 0, audit: 0, rare: 1, kept: 1 };

const str = (v) => (v == null ? "" : String(v));
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const lower = (v) => str(v).trim().toLowerCase();
const timeOf = (f) => parseServerTimestamp(f?.completedAt);
const idOf = (p) => lower(p?.finding?.id);
const minutes = (ms) => {
    const m = Math.max(1, Math.round(ms / 60_000));
    return m <= 120 ? `${m} min` : `${(m / 60).toFixed(1).replace(/\.0$/, "")} h`;
};

// The sessions in reach that did a kind of act, by the family's index.
const familySessions = (map, family) => {
    const f = map?.familyByIndex?.get?.(family) || map?.families?.find?.((x) => x.index === family);
    return f ? [...new Set((f.sessions || []).map(lower))] : [];
};
const familyName = (map, family) => str((map?.familyByIndex?.get?.(family) || map?.families?.find?.((x) => x.index === family))?.name).trim();

/**
 * The findings cases start from: Map(finding id -> { p, origin }), where `p`
 * is the placed finding (investigateFacts placeFindings) and `origin` the
 * first of standard, audit, rare, kept that marks it. `rare` false leaves rare
 * acts out (a workspace's tab), as does a reach of fewer than `rareMin`
 * sessions.
 */
export function seedsOf(placed, { map = null, investigation = null, rare = true, rareMin = RARE_MIN_SESSIONS } = {}) {
    const list = placed || [];
    const byId = new Map(list.map((p) => [idOf(p), p]));
    const seeds = new Map();
    const add = (p, origin) => {
        const id = idOf(p);
        if (p && id && !seeds.has(id)) seeds.set(id, { p, origin });
    };
    for (const p of list) if (p.group === "needs_you") add(p, "standard");
    const cases = investigation?.cases || [];
    for (const c of cases) {
        if (c.audit && c.dig?.status === "done" && c.dig.needsPerson === "yes") for (const id of c.findings) add(byId.get(id), "audit");
    }
    if (rare && new Set(list.map((p) => lower(p.finding.session))).size >= rareMin) {
        const rares = list
            .filter((p) => p.group === "slipped" && severityRank(p.finding.severity) >= RARE_SEVERITY && !seeds.has(idOf(p))
                && familySessions(map, p.finding.family).length === 1)
            .sort(comparePlaced);
        const sessions = new Set();
        for (const p of rares) {
            const s = lower(p.finding.session);
            if (!sessions.has(s) && sessions.size >= RARE_MAX) continue;
            sessions.add(s);
            add(p, "rare");
        }
    }
    // A case the reader kept stays while its findings are in reach; an audit
    // that came back with nothing for a person does not, unless they decided.
    for (const c of cases) {
        if (c.audit && !c.decision) continue;
        for (const id of c.findings) add(byId.get(id), "kept");
    }
    return seeds;
}

// What each session in reach is, from every finding the pool holds: the
// machine its findings name and when they were reviewed.
function sessionFacts(placed, map) {
    const out = new Map();
    const note = (f) => {
        const s = lower(f?.session);
        if (!s) return;
        const at = timeOf(f);
        const had = out.get(s) || { session: s, endpoint: null, first: Infinity, last: -Infinity };
        if (!had.endpoint && f.endpoint) had.endpoint = lower(f.endpoint);
        if (Number.isFinite(at)) { had.first = Math.min(had.first, at); had.last = Math.max(had.last, at); }
        out.set(s, had);
    };
    for (const p of placed || []) note(p.finding);
    for (const f of map?.findings?.values?.() || []) note(f);
    return out;
}

const gapOf = (a, b) => {
    if (!Number.isFinite(a?.first) || !Number.isFinite(b?.first)) return Infinity;
    return Math.max(0, Math.max(a.first, b.first) - Math.min(a.last, b.last));
};

// The sessions laid on a case's board, through `boardOf(caseId)`.
const boardSessions = (boardOf, id) => (boardOf ? (boardOf(id) || []).map(lower).filter(Boolean) : []);

/**
 * The cases over `placed` (every finding in reach, placed), most pressing
 * first. `map`: the pool's triage map (lib/triage.js), for kinds of act;
 * `investigation`: what the inbox keeps (investigationOf(INBOX_SCOPE));
 * `boardOf(caseId)`: the sessions on a kept case's board, if it has one;
 * `rare` and `rareMin` as seedsOf takes them.
 * Each case is a view as Investigate draws one (investigateCases caseView),
 * its items the seeds, with: `stored` (kept in the store), `origin` (what
 * marks its lead), `origins` (how many seeds each origin marks), `decision`,
 * `audit`, `grown` (sessions on its board, or that its digs named as part of
 * its story, that are not otherwise in it; part of `sessions`), `links` (what
 * joined its sessions: { a, b, kind, why }),
 * `nearby` (sessions near it but not in it: { session, kind, why, caseId }),
 * `audits` (the audits that sampled its findings: { id, kind, group, dig }),
 * `kinds` (the kinds of act its findings name) and `likeLetGo` (a case the
 * reader let go that did one of them: { id, title, kinds }, or null).
 */
export function formCases({ placed = [], map = null, investigation = null, boardOf = null, rare = true, rareMin = RARE_MIN_SESSIONS } = {}) {
    const seeds = [...seedsOf(placed, { map, investigation, rare, rareMin }).values()]
        .sort((a, b) => comparePlaced(a.p, b.p))
        .slice(0, SEEDS_MAX);
    const bySession = new Map();
    for (const s of seeds) {
        const sid = lower(s.p.finding.session);
        if (!sid) continue;
        if (!bySession.has(sid)) bySession.set(sid, []);
        bySession.get(sid).push(s);
    }
    const facts = sessionFacts(placed, map);

    // Sessions joined, each join said; one that would make a case too big is
    // refused and kept as nearby.
    const parent = new Map([...bySession.keys()].map((s) => [s, s]));
    const size = new Map([...bySession.keys()].map((s) => [s, 1]));
    const find = (x) => {
        let r = x;
        while (parent.get(r) !== r) r = parent.get(r);
        while (parent.get(x) !== r) { const next = parent.get(x); parent.set(x, r); x = next; }
        return r;
    };
    const links = [];
    const refused = [];
    const join = (a, b, kind, why) => {
        const ra = find(a);
        const rb = find(b);
        if (ra === rb) return;
        if (size.get(ra) + size.get(rb) > CASE_MAX_SESSIONS) { refused.push({ a, b, kind, why }); return; }
        const [big, small] = size.get(ra) > size.get(rb) || (size.get(ra) === size.get(rb) && ra < rb) ? [ra, rb] : [rb, ra];
        parent.set(small, big);
        size.set(big, size.get(big) + size.get(small));
        links.push({ a, b, kind, why });
    };
    const chain = (sessions, kind, why) => {
        const list = [...new Set(sessions)].filter((s) => bySession.has(s)).sort();
        for (let i = 1; i < list.length; i++) join(list[0], list[i], kind, why);
    };

    // What the reader put together: a kept case's sessions, and those on its board.
    const stored = [...(investigation?.cases || [])].sort((a, b) => str(a.id).localeCompare(str(b.id)));
    const storedOf = new Map();
    for (const c of stored) for (const id of c.findings) if (!storedOf.has(id)) storedOf.set(id, c);
    const placedById = new Map((placed || []).map((p) => [idOf(p), p]));
    for (const c of stored) {
        const sessions = c.findings.map((id) => lower(placedById.get(id)?.finding.session)).filter(Boolean);
        chain([...sessions, ...boardSessions(boardOf, c.id)], "kept", "You kept these together");
    }
    // The same kind of act, while few sessions in reach did it.
    const families = new Map();
    for (const s of seeds) {
        const fam = s.p.finding.family;
        if (fam == null) continue;
        if (!families.has(fam)) families.set(fam, new Set());
        families.get(fam).add(lower(s.p.finding.session));
    }
    const actLinks = [...families.entries()]
        .map(([fam, sessions]) => ({ fam, sessions: [...sessions], reach: familySessions(map, fam).length }))
        .filter((f) => f.sessions.length > 1 && f.reach <= FAMILY_MAX_SESSIONS)
        .sort((a, b) => (a.reach - b.reach) || (Number(a.fam) - Number(b.fam)));
    for (const f of actLinks) {
        const name = familyName(map, f.fam);
        chain(f.sessions, "act", name ? `Same kind of act: ${name}` : "Same kind of act");
    }
    // The same machine within the window, the closest first.
    const byMachine = new Map();
    for (const s of bySession.keys()) {
        const f = facts.get(s);
        if (!f?.endpoint || !Number.isFinite(f.first)) continue;
        if (!byMachine.has(f.endpoint)) byMachine.set(f.endpoint, []);
        byMachine.get(f.endpoint).push(f);
    }
    const machineLinks = [];
    for (const list of byMachine.values()) {
        list.sort((a, b) => (a.first - b.first) || a.session.localeCompare(b.session));
        for (let i = 1; i < list.length; i++) {
            const gap = gapOf(list[i - 1], list[i]);
            if (gap <= MACHINE_WINDOW_MS) machineLinks.push({ a: list[i - 1].session, b: list[i].session, gap });
        }
    }
    machineLinks.sort((x, y) => (x.gap - y.gap) || x.a.localeCompare(y.a) || x.b.localeCompare(y.b));
    for (const l of machineLinks) join(l.a, l.b, "machine", l.gap ? `Same machine, ${minutes(l.gap)} apart` : "Same machine, at the same time");

    // Each set of joined sessions is a case; the most pressing claims a kept
    // case's id first.
    const groups = new Map();
    for (const sid of bySession.keys()) {
        const root = find(sid);
        if (!groups.has(root)) groups.set(root, []);
        groups.get(root).push(sid);
    }
    const parts = [...groups.values()]
        .map((sessions) => ({ sessions, seeds: sessions.flatMap((s) => bySession.get(s)).sort((a, b) => comparePlaced(a.p, b.p)) }))
        .sort((a, b) => comparePlaced(a.seeds[0].p, b.seeds[0].p));
    const caseOfSession = new Map();
    const taken = new Set();
    const used = new Set(stored.map((c) => c.id));
    const cases = parts.map((part) => {
        const counts = new Map();
        for (const s of part.seeds) {
            const c = storedOf.get(idOf(s.p));
            if (c && !taken.has(c.id)) counts.set(c, (counts.get(c) || 0) + 1);
        }
        // An audit's own record stays its sample: a case takes its id only
        // when it is nothing but that audit's findings.
        const kept = [...counts.entries()]
            .filter(([c, n]) => !c.audit || n === part.seeds.length)
            .sort((a, b) => (b[1] - a[1]) || (b[0].at - a[0].at) || str(a[0].id).localeCompare(str(b[0].id)))[0]?.[0] || null;
        if (kept) taken.add(kept.id);
        const first = [...part.seeds].sort((a, b) => ((timeOf(a.p.finding) || 0) - (timeOf(b.p.finding) || 0)) || idOf(a.p).localeCompare(idOf(b.p)))[0];
        let id = kept?.id || `k:${idOf(first.p)}`;
        // A kept case holding this one's first finding went to another case.
        for (let n = 2; !kept && used.has(id); n++) id = `k:${idOf(first.p)}~${n}`;
        used.add(id);
        for (const s of part.sessions) caseOfSession.set(s, id);
        const audits = [...new Set(part.seeds.map((x) => storedOf.get(idOf(x.p))).filter((c) => c?.audit))]
            .map((c) => ({ id: c.id, kind: c.audit.kind, group: c.audit.group, dig: c.dig || null }));
        return { id, part, kept, audits };
    });

    const views = cases.map(({ id, part, kept, audits }) => {
        const view = caseView({
            id, title: "", account: kept?.account || "", by: kept?.by || "investigate", matches: [],
            dig: kept?.dig || null, notes: kept?.notes || [], related: kept?.related || [],
        }, part.seeds.map((s) => s.p));
        const origins = Object.fromEntries(ORIGINS.map((o) => [o, 0]));
        for (const s of part.seeds) origins[s.origin] += 1;
        // What the reader laid on its board, and what its digs named as part
        // of its story, are part of it.
        const inCase = new Set(view.sessions);
        const grown = [...new Set([...boardSessions(boardOf, id), ...(kept?.related || []).map((r) => lower(r.session))])].filter((s) => s && !inCase.has(s));
        const mine = new Set(part.sessions);
        return {
            ...view,
            // The kinds of act its findings name, to know a case like it again.
            kinds: [...new Set(part.seeds.map((x) => familyName(map, x.p.finding.family)).filter(Boolean))],
            likeLetGo: null,
            stored: !!kept,
            // The strongest that marks any of its findings.
            origin: ORIGINS.find((o) => origins[o] > 0),
            origins,
            decision: kept?.decision || null,
            audit: kept?.audit || null,
            audits,
            grown,
            sessions: [...view.sessions, ...grown],
            links: links.filter((l) => mine.has(l.a)),
            nearby: [],
        };
    });

    // Near each case but not in it: sessions a refused join would have
    // brought in, sessions that did the same rare kind of act, and sessions
    // on the same machine within the window.
    const byId = new Map(views.map((v) => [v.id, v]));
    const near = new Map(views.map((v) => [v.id, new Map()]));
    const offer = (caseId, session, kind, why, rank) => {
        const v = byId.get(caseId);
        if (!v || v.sessions.includes(session)) return;
        const list = near.get(caseId);
        const had = list.get(session);
        if (!had || rank < had.rank) list.set(session, { session, kind, why, caseId: caseOfSession.get(session) || null, rank });
    };
    for (const r of refused) {
        offer(caseOfSession.get(r.a), r.b, r.kind, r.why, 0);
        offer(caseOfSession.get(r.b), r.a, r.kind, r.why, 0);
    }
    for (const v of views) {
        for (const p of v.items) {
            const reach = familySessions(map, p.finding.family);
            if (reach.length < 2 || reach.length > FAMILY_MAX_SESSIONS) continue;
            const name = familyName(map, p.finding.family);
            for (const s of reach) offer(v.id, s, "act", name ? `Same kind of act: ${name}` : "Same kind of act", 1 + reach.length / 100);
        }
    }
    // Each machine's sessions in time order, with the longest one's span, so
    // a session's neighbours are a bounded stretch of its machine's list.
    const machines = new Map();
    for (const f of facts.values()) {
        if (!f.endpoint || !Number.isFinite(f.first)) continue;
        if (!machines.has(f.endpoint)) machines.set(f.endpoint, { list: [], span: 0 });
        const m = machines.get(f.endpoint);
        m.list.push(f);
        m.span = Math.max(m.span, f.last - f.first);
    }
    for (const m of machines.values()) m.list.sort((a, b) => (a.first - b.first) || a.session.localeCompare(b.session));
    for (const v of views) {
        for (const s of v.sessions) {
            const here = facts.get(s);
            const m = here?.endpoint ? machines.get(here.endpoint) : null;
            if (!m || !Number.isFinite(here.first)) continue;
            const from = here.first - MACHINE_WINDOW_MS - m.span;
            let lo = 0;
            let hi = m.list.length;
            while (lo < hi) {
                const mid = (lo + hi) >> 1;
                if (m.list[mid].first < from) lo = mid + 1;
                else hi = mid;
            }
            for (let i = lo; i < m.list.length && m.list[i].first <= here.last + MACHINE_WINDOW_MS; i++) {
                const other = m.list[i];
                if (other.session === s) continue;
                const gap = gapOf(here, other);
                if (gap <= MACHINE_WINDOW_MS) offer(v.id, other.session, "machine", gap ? `Same machine, ${minutes(gap)} apart` : "Same machine, at the same time", 2 + gap / MACHINE_WINDOW_MS);
            }
        }
    }
    for (const v of views) {
        v.nearby = [...near.get(v.id).values()]
            .sort((a, b) => (a.rank - b.rank) || a.session.localeCompare(b.session))
            .slice(0, NEARBY_MAX)
            .map(({ rank: _rank, ...n }) => n);
    }
    // A case the reader has not decided on that does a kind of act a case
    // they let go did: the most recent such case, and the kinds they share.
    const letGo = stored.filter((c) => c.decision?.kind === "let_go" && c.kinds?.length);
    for (const v of views) {
        if (v.decision || !v.kinds.length) continue;
        const like = letGo
            .filter((c) => c.id !== v.id)
            .map((c) => ({ c, kinds: c.kinds.filter((k) => v.kinds.includes(k)) }))
            .filter((x) => x.kinds.length)
            .sort((a, b) => (num(b.c.decision.at) - num(a.c.decision.at)) || str(a.c.id).localeCompare(str(b.c.id)))[0];
        if (like) v.likeLetGo = { id: like.c.id, title: like.c.title || "", kinds: like.kinds };
    }
    return views.sort(compareInbox);
}

// -- ranking -------------------------------------------------------------------

const DIG_RANK = { yes: 0, unsure: 1, none: 2, no: 3 };
const digRank = (c) => DIG_RANK[c.dig?.status === "done" ? c.dig.needsPerson || "none" : "none"] ?? 2;
const DECISION_RANK = { taken: 0, none: 1, let_go: 2 };
const decisionRank = (c) => DECISION_RANK[c.decision?.kind || "none"] ?? 1;
export const latestOf = (c) => (Number.isFinite(c.latestAt) ? c.latestAt : 0);
export const isLetGo = (c) => c.decision?.kind === "let_go";

// What needs a person first: what the reader has taken on, then what they
// have not decided on, and last what they let go. Inside each, what a dig
// said needs a person, what a dig could not settle, what nobody has dug and
// what a dig said can be let go (Investigate's own order); then what is not
// like a case the reader let go before what is; then what a standard or an
// audit raised before what only rarity did; then the loudest, the widest and
// the newest.
export function compareInbox(a, b) {
    return (decisionRank(a) - decisionRank(b))
        || (digRank(a) - digRank(b))
        || (Number(!!a.likeLetGo) - Number(!!b.likeLetGo))
        || ((ORIGIN_RANK[a.origin] ?? 1) - (ORIGIN_RANK[b.origin] ?? 1))
        || (severityRank(b.severity) - severityRank(a.severity))
        || (b.sessions.length - a.sessions.length)
        || (latestOf(b) - latestOf(a))
        || a.id.localeCompare(b.id);
}

// The case a board was opened for, from the board's id (`case:inbox:<case>`).
export const boardCaseId = (canvasId) => {
    const head = `case:${INBOX_SCOPE}:`;
    return str(canvasId).startsWith(head) ? str(canvasId).slice(head.length) : null;
};

// -- audits ----------------------------------------------------------------------

// What an audit samples: the findings that happened and were not escalated,
// by why. A random few of each, dug, measure how much of what the standards
// let through a person should have seen.
export const AUDIT_GROUPS = ["slipped", "allowed", "contained"];
export const AUDIT_PER_GROUP = 2;
// Rare acts an audit digs beside its random sample, when nobody has.
export const AUDIT_RARE = 2;

// FNV-1a, so a sample is the same for the same salt.
function hash(text) {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

/**
 * An audit's picks over `placed`: `random`, AUDIT_PER_GROUP findings of each
 * of AUDIT_GROUPS no case holds and no audit sampled before, chosen by `salt`
 * ({ group, items: the finding and its incident's others in that group }),
 * and `rare`, the rare-act cases in `cases` nobody has dug or decided.
 */
export function auditPicks(placed, { map = null, investigation = null, cases = [], salt = "", perGroup = AUDIT_PER_GROUP, rareMax = AUDIT_RARE } = {}) {
    const held = new Set([
        ...(investigation?.cases || []).flatMap((c) => c.findings),
        ...cases.flatMap((c) => c.findingIds.map(lower)),
    ]);
    const random = [];
    for (const group of AUDIT_GROUPS) {
        const pool = (placed || []).filter((p) => p.group === group && !held.has(idOf(p)))
            .map((p) => ({ p, h: hash(`${salt}|${idOf(p)}`) }))
            .sort((a, b) => (a.h - b.h) || idOf(a.p).localeCompare(idOf(b.p)));
        for (const { p } of pool.slice(0, perGroup)) {
            const incident = map?.incidentById?.get?.(map?.findings?.get?.(p.finding.id)?.incident);
            const mates = new Set((incident?.findingIds || []).map(lower));
            const items = [p, ...(placed || []).filter((q) => q !== p && q.group === group && mates.has(idOf(q)) && !held.has(idOf(q)))];
            for (const q of items) held.add(idOf(q));
            random.push({ group, items });
        }
    }
    const rare = cases.filter((c) => c.origin === "rare" && !c.dig && !c.decision).slice(0, rareMax);
    return { random, rare };
}

/**
 * What the random audits found, for the blind-spot readout: per group of
 * AUDIT_GROUPS, how many findings in reach the standards let through
 * (`findings`), how many audits sampled it (`sampled`), were dug (`dug`), and
 * of those how many a person should have seen (`caught`), were left unsure
 * (`unsure`) or let go (`letGo`); `pending` sampled and not yet dug. Every
 * random audit this browser kept counts, wherever it was sampled: the rate
 * is the standards', not the search's.
 */
export function auditReadout({ investigation = null, placed = [] } = {}) {
    const blank = (group) => ({ group, findings: 0, sampled: 0, dug: 0, caught: 0, unsure: 0, letGo: 0, pending: 0 });
    const groups = new Map(AUDIT_GROUPS.map((g) => [g, blank(g)]));
    for (const p of placed || []) if (groups.has(p.group)) groups.get(p.group).findings += 1;
    for (const c of investigation?.cases || []) {
        if (c.audit?.kind !== "random" || !groups.has(c.audit.group)) continue;
        const g = groups.get(c.audit.group);
        g.sampled += 1;
        if (c.dig?.status !== "done") { g.pending += 1; continue; }
        g.dug += 1;
        if (c.dig.needsPerson === "yes") g.caught += 1;
        else if (c.dig.needsPerson === "no") g.letGo += 1;
        else g.unsure += 1;
    }
    const list = [...groups.values()];
    const total = blank("all");
    for (const g of list) for (const k of Object.keys(total)) if (k !== "group") total[k] += g[k];
    return { groups: list, total };
}
